import { accountRoutes } from "./account";
import { checkoutRoutes } from "./checkout";
import type { RouteRecordRaw } from "vue-router";
import Error403 from "@/pages/403.vue";
import Error404 from "@/pages/404.vue";
import Error500 from "@/pages/500.vue";
import CareersJobDetailsPage from "@/pages/thorlabs/careers-job-details-page.vue";

const SingInPage = () => import("@/pages/sign-in.vue");
const SignUpPage = () => import("@/pages/sign-up.vue");
const ActivationSuccess = () => import("@/pages/activation-success.vue");
const ConfirmInvitation = () => import("@/pages/confirm-invitation.vue");
const ForgotPassword = () => import("@/pages/forgot-password.vue");
const ForgotId = () => import("@/pages/thorlabs/forgot-id.vue");
const ResetPassword = () => import("@/pages/reset-password.vue");
const ResetPasswordInitial = () => import("@/pages/reset-password-initial.vue");
const InfoResetPasswordInitial = () => import("@/pages/info-reset-password-initial.vue");
const ChangePassword = () => import("@/pages/change-password.vue");
const BlockedPage = () => import("@/pages/blocked.vue");
const Account = () => import("@/pages/account/index.vue");
const BulkOrder = () => import("@/pages/bulk-order.vue");
const CompareProducts = () => import("@/pages/compare-products.vue");
const Cart = () => import("@/pages/cart.vue");
const Search = () => import("@/pages/search.vue");
const Category = () => import("@/pages/category.vue");
const Product = () => import("@/pages/product.vue");
const ManualsPortal = () => import("@/pages/manuals-portal.vue");
const ReachCertificatePrint = () => import("@/pages/reach-print.vue");
const RohsCertificatePrint = () => import("@/pages/rohs-print.vue");
const TscaCertificatePrint = () => import("@/pages/tsca-print.vue");
const Branch = () => import("@/pages/branch.vue");
const Welcome = () => import("@/pages/welcome.vue");
const CustomTableForm = () => import("@/pages/thorlabs/custom-table-form-page.vue");
const Matcher = () => import("@/pages/matcher.vue");
const NewProducts = () => import("@/pages/new-products.vue");
const ReturnsForm = () => import("@/pages/order/returns-form.vue");
const PM5020Page = () => import("@/pages/thorlabs/PM5020.vue");
const PM60Page = () => import("@/pages/thorlabs/PM60.vue");
const PM100DPage = () => import("@/pages/thorlabs/PM100D.vue");

export const mainRoutes: RouteRecordRaw[] = [
  { path: "/403", name: "NoAccess", component: Error403, meta: { public: true } },
  { path: "/404", name: "NotFound", component: Error404, meta: { public: true } },
  { path: "/500", name: "InternalError", component: Error500, meta: { public: true } },
  { path: "/sign-in", name: "SignIn", component: SingInPage, meta: { public: true } },
  { path: "/sign-up", name: "SignUp", component: SignUpPage, meta: { public: true } },
  { path: "/confirm-invitation", name: "ConfirmInvitation", component: ConfirmInvitation, meta: { public: true } },
  { path: "/forgot-password", name: "ForgotPassword", component: ForgotPassword, meta: { public: true } },
  { path: "/forgot-id", name: "ForgotId", component: ForgotId, meta: { public: true } },
  { path: "/reset-password", name: "ResetPassword", component: ResetPassword, meta: { public: true } },
  {
    path: "/reset-password-initial",
    name: "ResetPasswordInitial",
    component: ResetPasswordInitial,
    meta: { public: true },
  },
  {
    path: "/info-reset-password-initial",
    name: "InfoResetPasswordInitial",
    component: InfoResetPasswordInitial,
    meta: { public: true },
    props: true,
  },
  { path: "/change-password", name: "ChangePassword", component: ChangePassword, meta: { public: false } },
  { path: "/set-password", name: "SetPassword", component: ResetPassword, meta: { public: true } },
  { path: "/blocked", name: "Blocked", component: BlockedPage, meta: { public: true } },
  {
    path: "/account/confirmemail",
    name: "ActivationSuccess",
    component: ActivationSuccess,
    meta: { public: true },
    props: true,
  },
  {
    path: "/account",
    name: "Account",
    component: Account,
    children: accountRoutes,
    redirect: { name: accountRoutes[0].name },
    meta: { requiresAuth: true },
  },
  { path: "/branch/:branchId", name: "BranchPage", component: Branch, props: true },
  {
    path: "/search",
    name: "Search",
    component: Search,
    children: [
      {
        path: "software",
        name: "SearchSoftware",
        component: Search,
      },
      {
        path: "technical-resources",
        name: "SearchTechnicalResources",
        component: Search,
      },
    ],
  },
  { path: "/bulk-order", name: "BulkOrder", component: BulkOrder },
  { path: "/compare", name: "CompareProducts", component: CompareProducts },
  { path: "/cart", name: "Cart", component: Cart },
  { path: "/successful-registration", name: "Welcome", component: Welcome, meta: { public: true } },
  { path: "/pm5020-demo", name: "PM5020", component: PM5020Page, meta: { public: true, layout: "Empty" } },
  { path: "/pm60-demo", name: "PM60", component: PM60Page, meta: { public: true, layout: "Empty" } },
  { path: "/pm100d-demo", name: "PM100D", component: PM100DPage, meta: { public: true, layout: "Empty" } },
  ...checkoutRoutes,
  { path: "/category/:categoryId", name: "Category", component: Category, props: true },
  { path: "/product/:productId", name: "Product", component: Product, props: true },
  { path: "/manuals", name: "ManualsPortal", component: ManualsPortal, props: true },
  { path: "/reach-print/:productId", name: "ReachCertificatePrint", component: ReachCertificatePrint, props: true },
  {
    path: "/reach-print/:productId/:currencyCode",
    name: "ReachCertificatePrintWithCurrency",
    component: ReachCertificatePrint,
    props: true,
  },
  { path: "/rohs-print/:productId", name: "RohsCertificatePrint", component: RohsCertificatePrint, props: true },
  {
    path: "/rohs-print/:productId/:currencyCode",
    name: "RohsCertificatePrintWithCurrency",
    component: RohsCertificatePrint,
    props: true,
  },
  { path: "/tsca-print/:productId", name: "TscaCertificatePrint", component: TscaCertificatePrint, props: true },
  {
    path: "/tsca-print/:productId/:currencyCode",
    name: "TscaCertificatePrintWithCurrency",
    component: TscaCertificatePrint,
    props: true,
  },

  { path: "/new-products", name: "NewProducts", component: NewProducts, props: true },
  { path: "/en/new-products", name: "NewProductsEn", component: NewProducts, props: true },

  { path: "/careers/job/:jobID", name: "CareersJobPage", component: CareersJobDetailsPage, props: true },
  {
    path: "/custom-table-form",
    name: "CustomTableForm",
    component: CustomTableForm,
  },

  /** NOTE: Always leave it last. */
  { path: "/:pathMatch(.*)*", name: "Matcher", component: Matcher, props: true },

  {
    path: "/rma/:orderId?",
    name: "ReturnsForm",
    component: ReturnsForm,
    props: true,
    meta: { public: true },
  },
];
